/**
 * 電子書籍変換API
 */

import { apiClient } from '@/api/client'
import type { ConversionOptions, ConversionProgressEvent } from '@/types/ebook-conversion'

/**
 * 変換完了レスポンス型
 */
export type EbookConversionCompletedResponse = {
  status: 'completed'
  message: string
  new_episode_id: number
  output_format: string
  file_size: number
}

/**
 * 削除レスポンス型
 */
export type DeleteConvertedEpisodeResponse = {
  success: boolean
  message: string
}

/**
 * 電子書籍変換をストリーミングで実行（Axios + onDownloadProgress）
 *
 * @param episodeId 変換する元EpisodeのDB ID
 * @param outputFormat 出力形式 ('pdf' | 'epub')
 * @param options 変換オプション
 * @param onMessage SSEメッセージ受信時のコールバック
 * @param onError エラー時のコールバック
 * @param signal AbortSignal
 */
export const streamEbookConversion = async (
  episodeId: number,
  outputFormat: 'pdf' | 'epub',
  options: ConversionOptions,
  onMessage: (data: ConversionProgressEvent) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
  customName?: string,
): Promise<void> => {
  let processedLength = 0

  try {
    await apiClient.get('/ebook/convert/stream', {
      params: {
        episode_id: episodeId,
        output_format: outputFormat,
        options: JSON.stringify(options),
        ...(customName ? { custom_name: customName } : {}),
      },
      responseType: 'text',
      signal,
      headers: {
        Accept: 'text/event-stream',
      },
      onDownloadProgress: (progressEvent) => {
        const responseText = progressEvent.event?.target?.response as string | undefined
        if (!responseText) return

        const newData = responseText.slice(processedLength)
        processedLength = responseText.length

        // SSE形式を解析
        const lines = newData.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6)
              const data = JSON.parse(jsonStr) as ConversionProgressEvent
              onMessage(data)
            } catch {
              // パースエラーは無視
            }
          }
        }
      },
    })
  } catch (error) {
    if (signal?.aborted) return
    onError(error instanceof Error ? error : new Error('Unknown error'))
  }
}

/**
 * 変換されたEpisodeを削除
 *
 * @param episodeId 削除するEpisodeのDB ID
 * @returns 削除結果
 */
export const deleteConvertedEpisode = async (episodeId: number): Promise<DeleteConvertedEpisodeResponse> => {
  const res = await apiClient.delete<DeleteConvertedEpisodeResponse>(`/ebook/episode/${episodeId}`)
  return res.data
}
